import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import PreviewIcon from '@mui/icons-material/Preview';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { useDeploy } from '../contexts/DeployContext';
import { generateDsl, parseDsl } from './notificationDsl';

const PRIORITY_OPTIONS = ['P0', 'P0,P1', 'P0,P1,P2'];
const COOLDOWN_BY_OPTIONS = ['token', 'wallet', 'market'];

function extractRootIndex(algorithm) {
  if (typeof algorithm !== 'string') return null;
  const m = algorithm.match(/\.index\(\s*['"]([\w-]+)['"]\s*\)/);
  return m ? m[1] : null;
}

// Eligible = algo whose root .index() is a notification-source. Catches both
// the friendly aliases (`<chain>-notifications`) and the underlying alpha
// indexes (`alpha-notifications-<chain>` and the `notification-<chain>-*`
// wallet aggregates that feed signal context).
function isNotificationSourceIndex(idx) {
  if (!idx) return false;
  return (
    idx.endsWith('-notifications') ||
    idx.startsWith('alpha-notifications-') ||
    idx.startsWith('notification-')
  );
}

const emptyForm = () => ({
  name: '',
  algo_ids: [],
  webhook_url: '',
  auth_header: '',
  daily_budget: 20,
  cooldown_hours: 2,
  cooldown_by: 'token',
  priority_filter: 'P0,P1',
});

function NotificationForm({ editing, onSaved, onClear }) {
  const { deployApi } = useDeploy();
  const [form, setForm] = useState(emptyForm());
  const [eligibleAlgos, setEligibleAlgos] = useState([]);
  const [algosLoading, setAlgosLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // DSL mode state
  const [mode, setMode] = useState('form'); // 'form' | 'dsl'
  const [dslText, setDslText] = useState('');
  const [dslError, setDslError] = useState(null);
  const editorContainerRef = useRef(null);
  const editorViewRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '',
        algo_ids: editing.algo_ids || [],
        webhook_url: editing.delivery?.webhook_url || '',
        auth_header: editing.delivery?.auth_header || '',
        daily_budget: editing.guards?.daily_budget ?? 20,
        cooldown_hours: editing.guards?.cooldown_hours ?? 2,
        cooldown_by: editing.guards?.cooldown_by || 'token',
        priority_filter: editing.guards?.priority_filter || 'P0,P1',
      });
    } else {
      setForm(emptyForm());
    }
    setFeedback(null);
    setError(null);
    setDslError(null);
    setMode('form');
  }, [editing]);

  useEffect(() => {
    let cancelled = false;
    setAlgosLoading(true);
    deployApi.listFeedAlgos()
      .then((res) => {
        if (cancelled) return;
        const all = res?.feed_algos || [];
        setEligibleAlgos(all.filter((a) => isNotificationSourceIndex(extractRootIndex(a.algorithm))));
      })
      .catch((err) => { if (!cancelled) setError(err?.message ?? 'Failed to load algos'); })
      .finally(() => { if (!cancelled) setAlgosLoading(false); });
    return () => { cancelled = true; };
  }, [deployApi]);

  // Index eligible algos by id + by name for DSL annotation / name resolution.
  const algosById = useMemo(() => {
    const m = new Map();
    for (const a of eligibleAlgos) m.set(Number(a.algo_id), a);
    return m;
  }, [eligibleAlgos]);
  const algosByName = useMemo(() => {
    const m = new Map();
    for (const a of eligibleAlgos) if (a?.name) m.set(a.name, a);
    return m;
  }, [eligibleAlgos]);

  // Mount CodeMirror when entering DSL mode; tear down when leaving.
  useEffect(() => {
    if (mode !== 'dsl' || !editorContainerRef.current) return undefined;
    const initial = generateDsl(form, { algosById });
    setDslText(initial);
    const state = EditorState.create({
      doc: initial,
      extensions: [
        javascript(),
        keymap.of(defaultKeymap),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const next = update.state.doc.toString();
            setDslText(next);
            setDslError(null);
          }
        }),
      ],
    });
    const view = new EditorView({ state, parent: editorContainerRef.current });
    editorViewRef.current = view;
    return () => {
      view.destroy();
      editorViewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleModeChange = useCallback((_e, next) => {
    if (next === mode) return;
    if (next === 'dsl') {
      setDslText(generateDsl(form, { algosById }));
      setDslError(null);
      setMode('dsl');
    } else {
      const parsed = parseDsl(dslText, { algosByName });
      if (!parsed.ok) {
        setDslError(parsed.error);
        return;
      }
      setForm((f) => ({ ...f, ...parsed.value }));
      setDslError(null);
      setMode('form');
    }
  }, [mode, form, dslText, algosById, algosByName]);

  const buildBody = useCallback((source) => {
    const delivery = {};
    if (source.webhook_url) delivery.webhook_url = source.webhook_url;
    if (source.auth_header) delivery.auth_header = source.auth_header;
    const guards = {};
    if (source.daily_budget !== '' && source.daily_budget != null) guards.daily_budget = Number(source.daily_budget);
    if (source.cooldown_hours !== '' && source.cooldown_hours != null) guards.cooldown_hours = Number(source.cooldown_hours);
    if (source.cooldown_by) guards.cooldown_by = source.cooldown_by;
    if (source.priority_filter) guards.priority_filter = source.priority_filter;
    if (source.per_type_limits) guards.per_type_limits = source.per_type_limits;
    return {
      name: source.name,
      algo_ids: source.algo_ids,
      delivery,
      guards,
    };
  }, []);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setFeedback(null);
    setDslError(null);
    try {
      // If on DSL tab, parse current code first and commit to form state.
      let source = form;
      if (mode === 'dsl') {
        const parsed = parseDsl(dslText, { algosByName });
        if (!parsed.ok) {
          setDslError(parsed.error);
          setBusy(false);
          return;
        }
        source = { ...form, ...parsed.value };
        setForm(source);
      }
      const body = buildBody(source);
      let result;
      if (editing?.notification_id) {
        result = await deployApi.notifications.update(editing.notification_id, body);
        setFeedback({ severity: 'success', message: `Notification ${result.notification_id} updated.` });
      } else {
        result = await deployApi.notifications.create(body);
        setFeedback({ severity: 'success', message: `Notification ${result.notification_id} created (paused). Activate to start delivery.` });
      }
      onSaved?.(result);
    } catch (err) {
      setError(err?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [buildBody, deployApi, editing, mode, form, dslText, algosByName, onSaved]);

  const handleActivate = useCallback(async () => {
    if (!editing?.notification_id) {
      setError('Save the notification before activating.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deployApi.notifications.activate(editing.notification_id);
      setFeedback({ severity: 'success', message: 'Notification activated.' });
      onSaved?.();
    } catch (err) {
      setError(err?.message ?? 'Activate failed');
    } finally {
      setBusy(false);
    }
  }, [deployApi, editing, onSaved]);

  const handleTest = useCallback(async () => {
    if (!editing?.notification_id) {
      setError('Save the notification before sending a test.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await deployApi.notifications.test(editing.notification_id);
      setFeedback({
        severity: result.ok ? 'success' : 'warning',
        message: `Test webhook → status ${result.status}. ${result.ok ? 'Endpoint accepted the payload.' : 'Endpoint did not accept.'}`,
      });
    } catch (err) {
      setError(err?.message ?? 'Test failed');
    } finally {
      setBusy(false);
    }
  }, [deployApi, editing]);

  const handlePreview = useCallback(async () => {
    if (!editing?.notification_id) {
      setError('Save the notification before running preview.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await deployApi.notifications.preview(editing.notification_id, { hours: 24 });
      const matches = Array.isArray(result?.matches) ? result.matches.length : 0;
      setFeedback({
        severity: 'info',
        message: result?.note
          ? `${result.note} Linked algos: ${result.algos?.length ?? 0}.`
          : `Preview: ${matches} matches in last 24h across ${result?.algos?.length ?? 0} algos.`,
      });
    } catch (err) {
      setError(err?.message ?? 'Preview failed');
    } finally {
      setBusy(false);
    }
  }, [deployApi, editing]);

  const algoLabel = (a) => `#${a.algo_id} · ${a.name} · ${extractRootIndex(a.algorithm) || 'unknown'}`;

  const selectedAlgoChips = useMemo(() => {
    const byId = new Map(eligibleAlgos.map((a) => [a.algo_id, a]));
    return (form.algo_ids || []).map((id) => byId.get(id) || { algo_id: id, name: `algo ${id}` });
  }, [eligibleAlgos, form.algo_ids]);

  const isNew = !editing?.notification_id;

  // For Save-disabled in DSL mode, we still want some signal that the doc has
  // a name + algos. We don't parse on every keystroke; require that the form
  // state (last synced) has name + algo_ids when on DSL too.
  const saveDisabled = busy || !form.name || form.algo_ids.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isNew ? 'New notification' : `Edit notification #${editing.notification_id}`}</Typography>
          {!isNew && (
            <Button size="small" onClick={onClear}>Close</Button>
          )}
        </Box>

        <Alert severity="info">
          Notifications are algorithm-backed configs. Choose saved algorithms, then set webhook,
          cooldown, priority, and budget delivery rules in the form or DSL.
        </Alert>

        <Tabs
          value={mode}
          onChange={handleModeChange}
          variant="standard"
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
        >
          <Tab value="form" label="Form" />
          <Tab value="dsl" label="DSL" />
        </Tabs>

        {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {dslError && <Alert severity="error" onClose={() => setDslError(null)}>DSL parse error: {dslError}</Alert>}

        {mode === 'form' ? (
          <>
            <TextField
              label="Name"
              size="small"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="algos-label">Algos</InputLabel>
              <Select
                labelId="algos-label"
                multiple
                value={form.algo_ids}
                onChange={(e) => setForm((f) => ({ ...f, algo_ids: e.target.value }))}
                input={<OutlinedInput label="Algos" />}
                renderValue={() => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedAlgoChips.map((a) => (
                      <Chip key={a.algo_id} size="small" label={`#${a.algo_id} ${a.name}`} />
                    ))}
                  </Box>
                )}
              >
                {algosLoading && <MenuItem disabled>Loading…</MenuItem>}
                {!algosLoading && eligibleAlgos.length === 0 && (
                  <MenuItem disabled>
                    No notification-source algos found. Build one in Deploy → Algos with a
                    root index ending in <code>-notifications</code> (e.g. <code>polymarket-notifications</code>).
                  </MenuItem>
                )}
                {eligibleAlgos.map((a) => (
                  <MenuItem key={a.algo_id} value={a.algo_id}>
                    <Checkbox checked={form.algo_ids.includes(a.algo_id)} />
                    <ListItemText primary={algoLabel(a)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider textAlign="left">Delivery</Divider>

            <TextField
              label="Webhook URL"
              size="small"
              value={form.webhook_url}
              onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))}
              fullWidth
              placeholder="https://customer.com/signals"
            />
            <TextField
              label="Auth header (optional)"
              size="small"
              value={form.auth_header}
              onChange={(e) => setForm((f) => ({ ...f, auth_header: e.target.value }))}
              fullWidth
              placeholder="Bearer ..."
            />

            <Divider textAlign="left">Guards</Divider>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Daily budget"
                type="number"
                size="small"
                value={form.daily_budget}
                onChange={(e) => setForm((f) => ({ ...f, daily_budget: e.target.value }))}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Cooldown hours"
                type="number"
                size="small"
                value={form.cooldown_hours}
                onChange={(e) => setForm((f) => ({ ...f, cooldown_hours: e.target.value }))}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Cooldown by"
                size="small"
                value={form.cooldown_by}
                onChange={(e) => setForm((f) => ({ ...f, cooldown_by: e.target.value }))}
                sx={{ flex: 1 }}
              >
                {COOLDOWN_BY_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Priority filter"
                size="small"
                value={form.priority_filter}
                onChange={(e) => setForm((f) => ({ ...f, priority_filter: e.target.value }))}
                sx={{ flex: 1 }}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </>
        ) : (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Edit the notification as an algo-dsl call chain. Switching back to <strong>Form</strong> will
              parse this code into the form fields. In <code>.algos([...])</code> you can use either
              numeric ids or string names — names resolve from the table below.
            </Typography>

            {eligibleAlgos.length > 0 && (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  p: 1,
                  maxHeight: 140,
                  overflowY: 'auto',
                  fontSize: 12,
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Your notification-source algos (click to copy id):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {eligibleAlgos.map((a) => {
                    const idx = extractRootIndex(a.algorithm) || 'unknown';
                    return (
                      <Chip
                        key={a.algo_id}
                        size="small"
                        label={`#${a.algo_id} · ${a.name} · ${idx}`}
                        onClick={() => {
                          try { navigator.clipboard?.writeText(String(a.algo_id)); } catch {}
                        }}
                        sx={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box
              ref={editorContainerRef}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                minHeight: 260,
                fontSize: 13,
                '& .cm-editor': { outline: 'none' },
                '& .cm-scroller': { fontFamily: 'monospace' },
              }}
            />
          </Box>
        )}

        <Divider />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={saveDisabled}
            onClick={handleSave}
          >
            {isNew ? 'Save' : 'Update'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={busy || isNew || editing?.status === 'active'}
            onClick={handleActivate}
          >
            Activate
          </Button>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            disabled={busy || isNew || !editing?.delivery?.webhook_url}
            onClick={handleTest}
            title={!editing?.delivery?.webhook_url ? 'Save a webhook URL first to send a test' : undefined}
          >
            Send test
          </Button>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            disabled={busy || isNew}
            onClick={handlePreview}
          >
            Show last 100
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default NotificationForm;
